import React, { useState } from "react";
import KlubbVelger, { getKlubbId } from "./KlubbVelger";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function DeliveryForm({ personId, productName, ean, onDelivered }) {
  const [quantity, setQuantity] = useState("1");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  async function submit(e) {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE_URL}/klubbsystem/trainers/${personId}/deliveries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ clubId: getKlubbId(), productName, ean, quantity: Number(quantity), note }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || "Kunne ikke registrere utlevering");
      }
      setNote("");
      onDelivered();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={submit} className="flex items-center gap-1.5">
      <input
        type="number"
        min="1"
        className="w-14 border border-gray-300 rounded-md px-1.5 py-1 text-xs"
        value={quantity}
        onChange={(e) => setQuantity(e.target.value)}
      />
      <input
        placeholder="Str. (valgfritt)"
        className="w-24 border border-gray-300 rounded-md px-1.5 py-1 text-xs"
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />
      <button
        type="submit"
        disabled={saving}
        className="px-2.5 py-1 rounded-md bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-semibold disabled:opacity-50"
      >
        {saving ? "…" : "Utlever"}
      </button>
      {error && <span className="text-red-600 text-xs">{error}</span>}
    </form>
  );
}

export default function Trenere() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searched, setSearched] = useState(false);

  async function search(q) {
    if (q.trim().length < 2) {
      setResults([]);
      setSearched(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await fetch(
        `${API_BASE_URL}/klubbsystem/trainers/search?q=${encodeURIComponent(q)}&clubId=${getKlubbId()}`
      );
      if (!res.ok) throw new Error("Kunne ikke søke etter trenere");
      setResults(await res.json());
      setSearched(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function handleChange(e) {
    const value = e.target.value;
    setQuery(value);
    search(value);
  }

  return (
    <div className="max-w-3xl mx-auto py-10 px-4">
      <span className="inline-block px-2.5 py-1 rounded-full bg-indigo-100 text-indigo-700 text-xs font-semibold mb-3">
        Klubb
      </span>
      <h1 className="text-2xl font-semibold text-gray-900 mb-1">Trenere</h1>
      <p className="text-gray-500 mb-6">
        Søk opp en trener for å se hvilken bekledning de har krav på, og registrer utlevering.
      </p>

      <KlubbVelger />

      <input
        autoFocus
        placeholder="Søk på navn, f.eks. Ola Nordmann"
        className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm mb-6"
        value={query}
        onChange={handleChange}
      />

      {loading && <p className="text-gray-400 text-sm">Søker …</p>}
      {error && <p className="text-red-600 text-sm">{error}</p>}

      {!loading && searched && results.length === 0 && (
        <p className="text-gray-400 text-sm">Ingen trenere funnet med det navnet.</p>
      )}

      <div className="space-y-4">
        {results.map((r) => (
          <div key={r.person._id} className="border border-gray-200 rounded-xl overflow-hidden">
            <div className="bg-gray-50 px-4 py-3">
              <p className="font-semibold text-gray-900">{r.person.name}</p>
              <p className="text-xs text-gray-500">
                {r.roles.map((role, i) => (
                  <span key={i}>
                    {role.title || "Trener"}{role.team ? ` · ${role.team.name}` : ""}
                    {i < r.roles.length - 1 ? ", " : ""}
                  </span>
                ))}
              </p>
            </div>
            {r.clothing.length === 0 ? (
              <p className="text-gray-400 text-sm px-4 py-3">Ingen bekledningsrettigheter registrert.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-gray-500 text-xs uppercase">
                    <tr>
                      <th className="text-left px-4 py-2">Produkt</th>
                      <th className="text-right px-4 py-2">Rettighet</th>
                      <th className="text-right px-4 py-2">Utlevert</th>
                      <th className="text-right px-4 py-2">Gjenstår</th>
                      <th className="text-left px-4 py-2">Registrer utlevering</th>
                    </tr>
                  </thead>
                  <tbody>
                    {r.clothing.map((c, i) => (
                      <tr key={i} className="border-t border-gray-100">
                        <td className="px-4 py-2 font-medium text-gray-900">{c.productName}</td>
                        <td className="px-4 py-2 text-right tabular-nums">{c.entitled}</td>
                        <td className="px-4 py-2 text-right tabular-nums text-gray-500">{c.delivered}</td>
                        <td className={`px-4 py-2 text-right tabular-nums font-medium ${c.remaining > 0 ? "text-emerald-600" : "text-gray-400"}`}>
                          {c.remaining}
                        </td>
                        <td className="px-4 py-2">
                          {c.remaining > 0 && (
                            <DeliveryForm
                              personId={r.person._id}
                              productName={c.productName}
                              ean={c.ean}
                              onDelivered={() => search(query)}
                            />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
